/**
 * Folds the community's Control Plane (CORD-02 §5): subscribes by the held
 * `control_pk`, opens each wrap with the member-held read key, and folds the
 * editions into metadata, channels, roles, grants, and the banlist.
 */

import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNostr } from "@nostrify/react";
import { useEffect } from "react";

import { controlReadKey } from "@/lib/concord/crypto";
import { decodeStreamEvent } from "@/lib/concord/stream";
import { foldControlPlane, type ControlState } from "@/lib/concord/editions";
import { communityRelays } from "@/hooks/useChama";
import type { Membership } from "@/lib/concord/types";

const EMPTY_CONTROL: ControlState = {
  metadata: null,
  channels: [],
  roles: new Map(),
  grants: new Map(),
  banlist: new Set(),
  heads: new Map(),
};

export function useControlPlane(membership: Membership | undefined) {
  const { nostr } = useNostr();
  const queryClient = useQueryClient();
  const communityId = membership?.communityId;
  const relays = communityRelays(membership);
  const relaysKey = relays.join(",");

  const query = useQuery({
    queryKey: ["chama", "control", communityId],
    enabled: Boolean(membership && membership.current.control_pk),
    queryFn: async (c) => {
      const m = membership!.current;
      const read = await controlReadKey(m.community_root, communityId!, m.root_epoch);
      const events = await nostr.group(relays).query(
        [{ kinds: [1059], authors: [m.control_pk], limit: 500 }],
        { signal: c.signal },
      );
      const decoded = events
        .map((e) => decodeStreamEvent(e, { pk: m.control_pk, convKey: read.convKey }))
        .filter((d): d is NonNullable<typeof d> => d !== null);
      return foldControlPlane(decoded, m.owner);
    },
    staleTime: 15_000,
  });

  // Live: new Control Plane wraps (renames, new channels, bans) refold.
  useEffect(() => {
    if (!membership) return;
    const m = membership.current;
    const controller = new AbortController();
    const since = Math.floor(Date.now() / 1000) - 5;
    (async () => {
      try {
        const sub = nostr
          .group(relays)
          .req([{ kinds: [1059], authors: [m.control_pk], since }], {
            signal: controller.signal,
          });
        for await (const msg of sub) {
          if (msg[0] === "EVENT") {
            void queryClient.invalidateQueries({
              queryKey: ["chama", "control", communityId],
            });
          }
        }
      } catch {
        // subscription closed or relay dropped; the next mount reopens it
      }
    })();
    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId, relaysKey]);

  return {
    control: query.data ?? EMPTY_CONTROL,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
